import React, { useEffect, useState } from 'react';
import { Modal, InputNumber, Input, Typography, Alert, Spin, Button, Space, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { message } from '../../services/toastMessage';
import {
  getProviderFeeTermsForUser,
  setProviderFeeTerms,
  clearProviderFeeTerms,
} from '../../services/AdminService';
import { IProviderFeeTerms } from '../../interfaces/heavyDutyMachinery/IProviderFeeTerms';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ProviderFeeTermsModalProps {
  userId?: string;
  /** Shown in the header so the admin can see who they are about to change. */
  userLabel?: string;
  /** The platform's own rate, so the numbers on screen mean something. */
  defaultPercent: number;
  defaultFixed: number;
  open: boolean;
  onClose: () => void;
  /** Called after a save or a clear, with the new terms or null for "standard rate". */
  onSaved: (userId: string, terms: IProviderFeeTerms | null) => void;
}

/**
 * What the platform charges one person.
 *
 * Its own component rather than a block inside the users section, because
 * the same decision gets made from more than one place -- a host's own page
 * and the service providers list are both obvious homes for it later, and a
 * second copy would drift the first time either was touched.
 *
 * Two things it is careful about, both of them money:
 *
 * "Back to the standard rate" is a separate action from typing the default
 * numbers in. On screen they look identical; they mean completely different
 * things the day the standard rate changes, and only one of them follows it.
 *
 * And nothing here reaches a booking already taken. Those carry the terms
 * they were sold under, frozen on their own lines. The note says so, because
 * an admin lowering somebody's commission will reasonably wonder whether it
 * applies to the job they agreed yesterday.
 */
const ProviderFeeTermsModal: React.FC<ProviderFeeTermsModalProps> = ({
  userId,
  userLabel,
  defaultPercent,
  defaultFixed,
  open,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [existing, setExisting] = useState<IProviderFeeTerms | null>(null);
  const [percent, setPercent] = useState<number | null>(defaultPercent);
  const [fixed, setFixed] = useState<number | null>(defaultFixed);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    getProviderFeeTermsForUser(userId)
      .then((terms) => {
        setExisting(terms);
        // Somebody on the standard rate opens with the standard numbers
        // already filled in, so the form starts from what is true today
        // rather than from zero -- which would be a real, meaningful value
        // typed by accident.
        setPercent(terms ? terms.percentOfAmount : defaultPercent);
        setFixed(terms ? terms.fixedAmount : defaultFixed);
        setReason(terms?.reason ?? '');
      })
      .catch(() => message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR)))
      .finally(() => setLoading(false));
  }, [open, userId, defaultPercent, defaultFixed, t]);

  const handleSave = async () => {
    if (!userId) return;
    if (percent == null || percent < 0 || percent > 100) {
      message.error(translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_PERCENT_INVALID));
      return;
    }
    if (fixed == null || fixed < 0) {
      message.error(translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_FIXED_INVALID));
      return;
    }
    if (!reason.trim()) {
      message.error(translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_REASON_REQUIRED));
      return;
    }

    setSaving(true);
    try {
      const saved = await setProviderFeeTerms(userId, {
        percentOfAmount: percent,
        fixedAmount: fixed,
        currencyCode: 'EUR',
        reason: reason.trim(),
      });
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      onSaved(userId, saved);
      onClose();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!userId) return;
    setClearing(true);
    try {
      await clearProviderFeeTerms(userId);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      onSaved(userId, null);
      onClose();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setClearing(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_TITLE)}
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          {/* Only offered to somebody who actually has terms to remove --
              "back to standard" on a user already on standard is a button
              that does nothing. */}
          <span>
            {existing && (
              <Button danger loading={clearing} onClick={handleClear}>
                {translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_CLEAR)}
              </Button>
            )}
          </span>
          <Space>
            <Button onClick={onClose}>{translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_CANCEL)}</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              {translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_SAVE)}
            </Button>
          </Space>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          {userLabel && (
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              {userLabel}
            </Text>
          )}
          <Paragraph type="secondary" style={{ fontSize: 13 }}>
            {translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_DEFAULT_NOTE, {
              percent: defaultPercent,
              fixed: defaultFixed,
            })}
          </Paragraph>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                {translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_PERCENT_LABEL)}
              </Text>
              <InputNumber
                value={percent}
                onChange={setPercent}
                min={0}
                max={100}
                step={0.1}
                addonAfter="%"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                {translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_FIXED_LABEL)}
              </Text>
              <InputNumber
                value={fixed}
                onChange={setFixed}
                min={0}
                step={0.05}
                addonAfter="€"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <Text style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
            {translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_REASON_LABEL)}
          </Text>
          <TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder={translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_REASON_PLACEHOLDER)}
            style={{ borderRadius: 12, marginBottom: 6 }}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
            {translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_REASON_HELP)}
          </Text>

          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 10, background: token.colorFillQuaternary }}
            message={translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_NOT_RETROACTIVE)}
          />
        </>
      )}
    </Modal>
  );
};

export default ProviderFeeTermsModal;
